import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, customerSession } from "@/lib/api";
import { CustomerHeader } from "@/components/tabdine/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const phoneValid = (p) => /^[6-9]\d{9}$/.test((p || "").trim());

export default function CustomerLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const returnTo = params.get("return_to");

  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [f, setF] = useState({ phone: "", name: "", password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const setField = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!phoneValid(f.phone)) errs.phone = "Enter a valid 10-digit Indian mobile number";
    if (!f.password || f.password.length < 6) errs.password = "Min 6 characters";
    if (mode === "signup") {
      if (!f.name.trim()) errs.name = "Please tell us your name";
      if (f.password !== f.confirm) errs.confirm = "Passwords don't match";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const url = mode === "login" ? "/customer/login" : "/customer/register";
      const body = mode === "login"
        ? { phone: f.phone, password: f.password }
        : { phone: f.phone, name: f.name, password: f.password };
      const res = await api.post(url, body);
      customerSession.set(res.data.token, res.data.customer);
      toast.success(mode === "login" ? "Welcome back!" : "Account created — welcome!");
      // Return-to logic: if signup started mid-order, hop back to restaurant menu (cart persists in localStorage)
      if (returnTo) {
        nav(returnTo);
      } else {
        nav("/");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-brand-light">
      <CustomerHeader />
      <div className="max-w-md mx-auto px-4 py-10">
        <h1 className="font-heading text-3xl font-bold text-brand-dark">
          {mode === "login" ? "Welcome back!" : "Create your account"}
        </h1>
        <p className="text-brand-dark/60 mt-1 text-sm">
          {mode === "login"
            ? "Sign in to see your orders and loyalty stamps."
            : "Just a phone number and password — that's it."}
        </p>
        <div className="mt-6 bg-white rounded-2xl border border-brand-dark/5 p-5 space-y-3">
          <div>
            <label className="text-xs text-brand-dark/60">Phone</label>
            <Input value={f.phone} onChange={setField("phone")} data-testid="phone-input" placeholder="10-digit mobile" inputMode="numeric" />
            {errors.phone && <div className="text-xs text-red-500 mt-1" data-testid="phone-error">{errors.phone}</div>}
          </div>
          {mode === "signup" && (
            <div>
              <label className="text-xs text-brand-dark/60">Full name</label>
              <Input value={f.name} onChange={setField("name")} data-testid="name-input" />
              {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
            </div>
          )}
          <div>
            <label className="text-xs text-brand-dark/60">Password</label>
            <Input type="password" value={f.password} onChange={setField("password")} data-testid="password-input" />
            {errors.password && <div className="text-xs text-red-500 mt-1">{errors.password}</div>}
          </div>
          {mode === "signup" && (
            <div>
              <label className="text-xs text-brand-dark/60">Confirm password</label>
              <Input type="password" value={f.confirm} onChange={setField("confirm")} data-testid="confirm-input" />
              {errors.confirm && <div className="text-xs text-red-500 mt-1" data-testid="confirm-error">{errors.confirm}</div>}
            </div>
          )}
          <Button onClick={submit} disabled={busy} className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="auth-submit">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
          <button
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErrors({}); }}
            className="text-xs text-brand-dark/60 hover:text-brand-orange w-full text-center"
            data-testid="switch-mode"
          >
            {mode === "login" ? "New here? Create an account →" : "Already have an account? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
